import { useState, useCallback, useLayoutEffect, useEffect } from 'react';

const FLIP_THRESHOLD = 240;
const MAX_HEIGHT_CAP = 420;
const EMPTY_DEPS = [];

/**
 * Shared positioning for `position: fixed` popovers anchored to a trigger
 * element and rendered in a portal (CompactSelectPicker, EmojiReactionPicker).
 *
 * `placement`: 'auto' flips between growing down and growing up based on
 * measured space below/above the anchor (dropdowns anchored anywhere on the
 * page); 'top' / 'bottom' force a direction for triggers that are always
 * anchored near one edge of the window and never need to flip.
 *
 * `maxHeight` is capped at whatever space is actually available on the
 * chosen side — never floored above it — so the popover can't be told to
 * render taller than the viewport has room for.
 *
 * `deps`: extra values to re-measure on while open, beyond resize/scroll —
 * e.g. a trigger's label text, which can change the anchor's rendered width
 * (and so the popover's position) without `open` itself changing, such as
 * when a picker's `value` is updated externally (a realtime collab echo)
 * while the popover is open.
 */
export default function usePopoverPosition({
  anchorRef,
  open,
  width,
  placement = 'auto',
  align = 'start',
  gap = 6,
  edgeMargin = 8,
  bounds,
  deps = EMPTY_DEPS,
}) {
  const [pos, setPos] = useState({ dir: 'down', top: 0, bottom: 0, left: 0, width: 0, maxHeight: 320 });

  const measure = useCallback(() => {
    if (!anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const w = typeof width === 'function' ? width(r) : width;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left;
    if (align === 'end') {
      const boundsRect = typeof bounds === 'function' ? bounds() : null;
      const minLeft = (boundsRect?.left ?? edgeMargin) + edgeMargin;
      const maxLeft = (boundsRect ? boundsRect.right : vw) - w - edgeMargin;
      left = Math.min(Math.max(r.right - w, minLeft), maxLeft);
    } else {
      left = r.left + w > vw - edgeMargin ? Math.max(edgeMargin, r.right - w) : r.left;
    }

    const spaceBelow = vh - r.bottom - edgeMargin;
    const spaceAbove = r.top - edgeMargin;
    let dir;
    if (placement === 'top') dir = 'up';
    else if (placement === 'bottom') dir = 'down';
    else dir = spaceBelow < FLIP_THRESHOLD && spaceAbove > spaceBelow ? 'up' : 'down';

    const available = dir === 'up' ? spaceAbove : spaceBelow;
    const maxHeight = available > 0 ? Math.min(available, MAX_HEIGHT_CAP) : 160;

    setPos(
      dir === 'up'
        ? { dir, left, width: w, bottom: vh - r.top + gap, maxHeight }
        : { dir, left, width: w, top: r.bottom + gap, maxHeight }
    );
  }, [anchorRef, width, placement, align, gap, edgeMargin, bounds]);

  useLayoutEffect(() => {
    if (!open) return;
    measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, measure, ...deps]);

  useEffect(() => {
    if (!open) return;
    const onResizeScroll = () => measure();
    window.addEventListener('resize', onResizeScroll);
    window.addEventListener('scroll', onResizeScroll, true);
    return () => {
      window.removeEventListener('resize', onResizeScroll);
      window.removeEventListener('scroll', onResizeScroll, true);
    };
  }, [open, measure]);

  return pos;
}
